/**
 * What the device last said about a mapping, kept as a report rather than an
 * answer.
 *
 * The firmware acknowledges a mapping write with `mappingack` and answers a read
 * with `rcumappingstatus`, and it attaches nothing to either that would tie it to
 * the request that caused it — no trigger, no sequence number, no echo of what
 * was sent. So the console cannot honestly claim "this is the reply to your last
 * command". It can show what it heard, when it heard it, and on which topic, and
 * let the technician judge.
 *
 * The payload is carried **verbatim**. The feature does not parse it: it shows
 * the text, plus the one fact the link's own decoder already extracted, so a
 * reply this console has no model for is still visible instead of swallowed.
 */
export const REPLY_VERDICT = {
  /** A mapping write was acknowledged as ok. */
  ACCEPTED: 'accepted',
  /** A mapping write was acknowledged as failed. */
  REFUSED: 'refused',
  /** A mapping read came back; whether it held anything is read from the text. */
  READ: 'read',
  /** Something else the device said about mappings. */
  OTHER: 'other',
} as const;

export type ReplyVerdict = (typeof REPLY_VERDICT)[keyof typeof REPLY_VERDICT];

/** How each verdict reads in the console, as the claim it actually supports. */
export const REPLY_VERDICT_LABEL = {
  [REPLY_VERDICT.ACCEPTED]: 'The device acknowledged a mapping write as ok.',
  [REPLY_VERDICT.REFUSED]: 'The device acknowledged a mapping write as failed.',
  [REPLY_VERDICT.READ]: 'The device answered a mapping read.',
  [REPLY_VERDICT.OTHER]: 'The device said something about mappings.',
} as const satisfies Record<ReplyVerdict, string>;

/**
 * What the console's link can say about the silence.
 *
 * The feature cannot see the link, so the tab hands it these three facts: the
 * topic answers arrive on, whether the tablet is subscribed to it, and — when it
 * is not — the link's own words for the trouble. Without them an empty reply
 * block reads as "the device said nothing", which is the one thing silence does
 * not prove.
 */
export interface KeypadLink {
  /** The device's answer topic, e.g. `888854/toCloudRCU`. */
  readonly answerTopic: string;
  /** True when the tablet is subscribed to that topic right now. */
  readonly listening: boolean;
  /**
   * The link's own words when it is not listening, e.g. the broker's error. Not
   * shown while listening, so a healthy link never carries stale trouble.
   */
  readonly trouble: string;
}

export interface KeypadReply {
  /** When the message arrived, so a stale reply cannot pass for a fresh one. */
  readonly at: string;
  readonly topic: string;
  /** The payload exactly as it arrived. */
  readonly text: string;
  readonly verdict: ReplyVerdict;
}

/** True when the verdict is bad news worth the colour it is shown in. */
export const isRefusal = (reply: KeypadReply): boolean =>
  reply.verdict === REPLY_VERDICT.REFUSED;
